import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { Preferences } from '@capacitor/preferences';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/services/api';

/**
 * On native platforms, after auth: request push permission, register with
 * APNs/FCM, and post the resulting token to the backend.
 *
 * Web is a no-op. Permission denial is a no-op (the user can opt back in
 * later from system settings; we'll re-request on next launch).
 */
export function useNativePushRegistration() {
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!Capacitor.isNativePlatform()) return;

    let cancelled = false;
    let registrationHandler: { remove: () => void } | null = null;

    (async () => {
      try {
        let perm = await PushNotifications.checkPermissions();
        if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
          perm = await PushNotifications.requestPermissions();
        }
        if (perm.receive !== 'granted' || cancelled) return;

        registrationHandler = await PushNotifications.addListener('registration', async (token) => {
          if (cancelled) return;
          try {
            // Persist locally so AuthContext.logout can include it in the
            // revoke call (B-064). Without this the backend's M3.5
            // revoke-on-logout logic has no token to act on.
            await Preferences.set({ key: 'device_token', value: token.value });
            await api.devices.register({
              platform: Capacitor.getPlatform() as 'ios' | 'android',
              token: token.value,
              app_version: import.meta.env.VITE_APP_VERSION ?? '0.0.0-0',
            });
          } catch (e) {
            console.error('[push] register failed', e);
          }
        });

        await PushNotifications.register();
      } catch (e) {
        console.error('[push] init failed', e);
      }
    })();

    return () => {
      cancelled = true;
      registrationHandler?.remove();
    };
  }, [isAuthenticated]);
}
